import styles from './drawer.module.scss';
import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import classnames from 'classnames';
import { useAuth, usePremium, useUser } from 'contexts';
import Routes, { sidebarRoutes } from 'data/routes';
import { SubscriptionStatusLabels } from 'data/enums';
import { usePathname, useRouter } from 'next/navigation';
import { Confirm, Flex, Icon, Image, Loading } from 'ui';

const Menu = ({ open, onClose }) => {
  const { isAuthenticated, logout, user } = useAuth();
  const { plan, ...manager } = usePremium();
  const { loading, profile } = useUser();

  const pathname = usePathname();
  const router = useRouter();

  if (!open) return null;

  const handleRedirect = (path) => {
    router.push(path);
    onClose();
  };

  return (
    <Stack className={styles.root}>
      {loading ? (
        <Box className={styles.loading}>
          <Loading loading={loading} />
        </Box>
      ) : (
        <>
          <Stack className={styles.account} spacing={3}>
            <Flex
              className={styles.item}
              spacing={0.5}
              onClick={() => handleRedirect(Routes.account)}
            >
              {profile.name ? (
                profile.photoURL ? (
                  <Image
                    alt="profile"
                    className={styles.profile}
                    height={60}
                    loading="eager"
                    src={profile.photoURL}
                    width={60}
                  />
                ) : (
                  <Avatar className={styles.profile}>
                    {profile.name.charAt(0)}
                  </Avatar>
                )
              ) : (
                <Icon
                  className={classnames(styles.profile, styles.circle)}
                  name="account_circle"
                />
              )}
              <Stack spacing={0.5}>
                <Typography variant="h6">
                  {profile.name || 'Invitado'}
                </Typography>
                <Typography variant="span">{user?.email}</Typography>
              </Stack>
            </Flex>
            <Flex
              className={styles.item}
              hidden={!manager.hasSubscription || !isAuthenticated}
            >
              <Flex
                className={styles.subscription}
                justify="space-between"
                onClick={() => {
                  if (!manager.hasSubscription) {
                    handleRedirect(Routes.subscription);
                  }
                }}
              >
                <Typography variant="body1">
                  {plan?.reason}
                  <span className={styles.status}>
                    {SubscriptionStatusLabels[manager?.status]}
                  </span>
                </Typography>
                <Typography variant="span">
                  {manager?.availableCredits} créditos
                </Typography>
              </Flex>
              <Flex
                className={styles.register}
                onClick={() => handleRedirect(Routes.register)}
                hidden={isAuthenticated}
              >
                <Typography variant="body1">Únete a Monus</Typography>
              </Flex>
            </Flex>
          </Stack>
          <Stack className={styles.container}>
            <Stack className={styles.menu} spacing={0.5}>
              {sidebarRoutes.map(({ href, label, icon }) => (
                <Flex
                  key={href}
                  className={classnames(styles.item, {
                    [styles.active]: pathname === href,
                  })}
                  spacing={0.5}
                  onClick={() => handleRedirect(href)}
                >
                  <Icon name={icon} />
                  <Typography variant="span">{label}</Typography>
                </Flex>
              ))}
            </Stack>
            <Divider />
            <Stack className={styles.menu} spacing={0.5}>
              <Flex
                className={classnames(styles.item, {
                  [styles.active]: pathname === Routes.create,
                })}
                spacing={0.5}
                onClick={() => handleRedirect(Routes.create)}
              >
                <Icon name="add_circle_outline" />
                <Typography variant="span">Crear historia</Typography>
              </Flex>
              <Confirm
                title="Invita y gana"
                description={[
                  '¡Invita a tus amigos a usar Monus y gana dinero real! Ganarás hasta $3.000 por cada amigo que se suscriba por primera vez a Monus.',
                  `${!profile?.handle && 'Para obtener un enlace de invitación ingresa a mi cuenta y elige un nombre de usuario.'}`,
                ]}
                confirmText={
                  profile?.handle ? 'Copiar enlace' : 'Ir a mi cuenta'
                }
                onConfirm={() =>
                  profile?.handle
                    ? navigator.clipboard.writeText(
                        process.env.MONUS_REFFERAL + '/' + profile?.handle
                      )
                    : handleRedirect(Routes.account)
                }
              >
                <Flex className={styles.item} spacing={0.5}>
                  <Icon name="person_add_alt" />
                  <Typography variant="span">Invitar amigos</Typography>
                </Flex>
              </Confirm>
              <Flex
                className={styles.item}
                hidden={!profile.name}
                onClick={() => {
                  logout();
                  onClose();
                  handleRedirect(Routes.home);
                }}
                spacing={0.5}
              >
                <Icon name="logout" />
                <Typography variant="span">Cerrar sesión</Typography>
              </Flex>
              <Flex
                className={styles.item}
                hidden={profile.name}
                onClick={() => handleRedirect(Routes.login)}
                spacing={0.5}
              >
                <Icon name="login" />
                <Typography variant="span">Iniciar sesión</Typography>
              </Flex>
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default Menu;
